// Pure, framework-agnostic helpers for the deals-list APPT.DATE cell.
//
// Extracted from src/views/deals/DealsList.tsx so the load-bearing invariant
// can be locked by a unit test (src/lib/deals/apptDate.test.ts) and never
// regress silently. See docs/REGRESSIONS.md → "deals-list no-show-appt-blank".
//
// THE INVARIANT (recurring bug): the ONLY valid source for the appointment
// date is the live `estimate_appointment`. The historical/forecast
// `close_date` must NEVER masquerade as the appointment date. When a deal's
// appointment has been cleared — No-Show/Reschedule, Lost, or any stage where
// the appointment is no longer live — the cell renders BLANK. Falling back to
// close_date for those rows is the regression this guard prevents.

export type ApptDeal = {
  stage?: string | null
  estimateAppointment?: string | null
  closeDate?: string | null
}

// Stages where the appointment is no longer live, so close_date must NOT be
// used as a fallback appointment source — the cell is blank instead. These are
// the terminal / appointment-cleared stages from the deals pipeline.
export const APPT_CLEARED_STAGES: ReadonlySet<string> = new Set([
  'NO_SHOW_RESCHEDULE',
  'LOST',
])

function sanitize(v: string | null | undefined): string | null {
  if (v == null) return null
  const s = String(v).trim()
  return s.length > 0 ? s : null
}

// Resolve the string the APPT.DATE cell should render from — or null when the
// cell must be blank. Live estimate_appointment always wins. close_date is a
// legacy fallback ONLY for non-cleared stages; for No-Show/Reschedule, Lost,
// or any cleared-appointment deal it is never used (returns null → blank cell).
export function resolveApptDateSource(deal: ApptDeal | null | undefined): string | null {
  if (!deal) return null
  const appt = sanitize(deal.estimateAppointment)
  if (appt) return appt
  // No live appointment. If the appointment was cleared (no-show/reschedule/
  // lost), the cell is BLANK — never leak close_date as the appointment date.
  const stage = sanitize(deal.stage)
  if (stage && APPT_CLEARED_STAGES.has(stage)) return null
  // Non-cleared stage with no live appointment: preserve the legacy close_date
  // fallback so historically-imported rows still show a date.
  return sanitize(deal.closeDate)
}

// Extracts a 12h "h:mm AM/PM" label from an appointment string ONLY when the
// value carries a real time component (e.g. '2026-06-25T14:30' or
// '2026-06-25 14:30:00'). Date-only values (and midnight 00:00, which is what a
// date-only legacy close_date degrades to) return null so the cell shows the
// date alone instead of inventing "12:00 AM". String-parsed (no Date()) to
// avoid timezone drift across the midnight boundary.
export function apptTimeLabel(src: string | null | undefined): string | null {
  if (!src) return null
  const m = String(src).match(/[T ](\d{2}):(\d{2})/)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (Number.isNaN(h) || Number.isNaN(min)) return null
  if (h === 0 && min === 0) return null // midnight => treat as date-only
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12; if (h === 0) h = 12
  return `${h}:${String(min).padStart(2, '0')} ${ampm}`
}
